<template>
  <div>
    <v-form>
      <v-row>
        <v-col>
          <h3 class="text-left mt-10">Edit company information</h3>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <p>Company Name</p>
          <v-text-field
            v-model="state.updatedTrustGroup.company.companyName"
            :error="v.updatedTrustGroup.company.companyName.$error"
            :error-messages="
              v.updatedTrustGroup.company.companyName.$errors[0]?.$message.toString()
            "
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col>
          <p>Organisation Number</p>
          <v-text-field
            v-model="state.updatedTrustGroup.company.organisationNumber"
            :error="v.updatedTrustGroup.company.organisationNumber.$error"
            :error-messages="
              v.updatedTrustGroup.company.organisationNumber.$errors[0]?.$message.toString()
            "
            variant="outlined"
            density="compact"
          />
        </v-col>
        <v-col>
          <p>Operating Country</p>
          <PAutocomplete
            :items="
              itemsStore.countries.map((c) => ({
                id: c.code,
                text: c.name,
              }))
            "
            :select-text="updatedTrustGroup.company.operatingCountryCode"
            :error="v.updatedTrustGroup.company.operatingCountryCode.$error"
            :error-messages="
              v.updatedTrustGroup.company.operatingCountryCode.$errors[0]?.$message.toString()
            "
            @field-updated="(val: string) => updatedTrustGroup.company.operatingCountryCode = val"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="4">
          <h3 class="text-left mb-4">Edit trust group type</h3>
          <PAutocomplete
            :items="trustGroupTypes"
            :select-text="state.updatedTrustGroup.trustGroupType"
            :error="v.updatedTrustGroup.trustGroupType.$error"
            :error-messages="
              v.updatedTrustGroup.trustGroupType.$errors[0]?.$message.toString()
            "
            @field-updated="(val: string) => (state.updatedTrustGroup.trustGroupType = val as TrustGroupType)"
          />
        </v-col>
      </v-row>
      <v-row class="mt-5 mb-5">
        <PSecondaryButton class="ml-3" @click="emit('cancel')"
          >Cancel</PSecondaryButton
        >
        <v-spacer></v-spacer>
        <PPrimaryButton class="mr-3" @click="putTrustGroupInfo"
          >Update Trust Group</PPrimaryButton
        >
      </v-row>
    </v-form>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import PPrimaryButton from "@/components/custom/buttons/PPrimaryButton.vue";
import PSecondaryButton from "@/components/custom/buttons/PSecondaryButton.vue";
import { genericError } from "@/modules/utilities";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";
import { useItemsStore } from "@/stores/itemsStore";
import type { TrustGroup } from "@/models/trustGroup";
import { updateTrustGroup } from "@/services/userProfileAdminService";
import { useSnackBarStore } from "@/stores/snackBarStore";
import { TrustGroupType } from "@/models/enum/trustGroupType";
import PAutocomplete from "@/components/custom/PAutocomplete.vue";

const itemsStore = useItemsStore();
const snackbarStore = useSnackBarStore();

const props = defineProps<{
  trustGroup: TrustGroup;
}>();

const emit = defineEmits<{
  (e: "cancel"): void;
  (e: "updated", trustGroup: TrustGroup): void;
}>();

const updatedTrustGroup = ref({ ...props.trustGroup });

const trustGroupTypes = new Array<string>(
  TrustGroupType.Supplier,
  TrustGroupType.Channel1Customer,
  TrustGroupType.Channel2Customer
);

const state = ref({
  updatedTrustGroup,
});

const rules = {
  updatedTrustGroup: {
    company: {
      companyName: { required },
      organisationNumber: { required },
      operatingCountryCode: { required },
    },
    trustGroupType: { required },
  },
};

const v = useVuelidate(rules, state);

async function putTrustGroupInfo(): Promise<void> {
  const validForm = await v.value.$validate();
  if (!validForm) {
    snackbarStore.showError("Invalid company");
  } else {
    try {
      await updateTrustGroup(updatedTrustGroup.value);
      snackbarStore.showSuccess("Successfully updated company");
      emitTrustGroup();
    } catch (error) {
      snackbarStore.showError(genericError);
    }
  }
}

function emitTrustGroup() {
  emit("updated", updatedTrustGroup.value);
}
</script>
